import { useEffect, useState, useCallback, useRef, type CSSProperties } from "react"
import { Link, useSearchParams } from "react-router-dom"
import { SEO } from '@alascorporation/seo'
import { siteConfig } from '../config/seo.config'
import {
  Search,
  SlidersHorizontal,
  Users,
  X,
} from "lucide-react"
import api from "../lib/api"
import { programs } from "../config/schools"
import { useAuthStore } from "../stores/authStore"
import { CourseCard, type CourseCardCourse, type CourseCardEnrollment } from "../components/academy/CourseCard"
import { ActiveFiltersBar } from "../components/academy/ActiveFiltersBar"
import { PageEmpty, PageHeader } from "@/components/page"

interface Course extends CourseCardCourse {
  short_description: string
  description: string
}

interface EnrolledCourseSummary extends CourseCardCourse {
  completed_lessons: number
  total_lessons: number
}

interface SeedbedInfo {
  seedbedName: string
  institutionName: string
}

interface SeedbedAccessResponse {
  seedbeds: SeedbedInfo[]
  courses: CourseCardCourse[]
}


export default function CourseCatalog() {
  const [searchParams] = useSearchParams()
  const [courses, setCourses] = useState<Course[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [search, setSearch] = useState("")
  // Primera tanda de resultados: entrada escalonada; los cambios de filtro/página usan fundido corto.
  const introClass = useRef(new Map<string, string>())
  const staggerUsed = useRef(false)
  const [filterCategory, setFilterCategory] = useState("")
  const [filterDifficulty, setFilterDifficulty] = useState("")
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalResults, setTotalResults] = useState(0)
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [enrollmentByCourseId, setEnrollmentByCourseId] = useState<Record<string, CourseCardEnrollment>>({})
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  // Logged in, /courses shows "mis cursos" by default — this escape hatch
  // (?view=all) is the only way back to the public buy-a-course catalog,
  // so an enrolled student isn't dead-ended when they want something new.
  const forcePublicView = searchParams.get("view") === "all"

  const [seedbedCourses, setSeedbedCourses] = useState<CourseCardCourse[]>([])
  const [seedbedInfo, setSeedbedInfo] = useState<SeedbedInfo[]>([])
  const [seedbedAccessChecked, setSeedbedAccessChecked] = useState(false)
  const [enrolledCourses, setEnrolledCourses] = useState<EnrolledCourseSummary[]>([])
  const [enrolledAccessChecked, setEnrolledAccessChecked] = useState(false)

  // Logged in, /courses stops being the public buy-a-course catalog and
  // becomes "my courses" — only what this student is actually enrolled in
  // (directly or via a seedbed), never the full public listing.
  const isPersonalizedMode = isAuthenticated && !forcePublicView && seedbedAccessChecked && enrolledAccessChecked
  const personalizedCourses = (() => {
    const byId = new Map<string, CourseCardCourse>()
    for (const c of seedbedCourses) byId.set(c.id, c)
    for (const c of enrolledCourses) if (!byId.has(c.id)) byId.set(c.id, c)
    return Array.from(byId.values())
  })()

  useEffect(() => {
    const urlSearch = searchParams.get("search") ?? ""
    const urlCategory = searchParams.get("category") ?? ""
    setSearch(urlSearch)
    setFilterCategory(urlCategory)
    setPage(1)
  }, [searchParams])

  useEffect(() => {
    if (!isAuthenticated) {
      setSeedbedCourses([])
      setSeedbedInfo([])
      setSeedbedAccessChecked(true)
      return
    }
    setSeedbedAccessChecked(false)
    api
      .get("/academy/seedbeds/me")
      .then(({ data }) => {
        const access = data.data as SeedbedAccessResponse
        setSeedbedCourses(access.courses ?? [])
        setSeedbedInfo(access.seedbeds ?? [])
      })
      .catch((err) => {
        console.error("Error loading seedbed access:", err)
        setSeedbedCourses([])
        setSeedbedInfo([])
      })
      .finally(() => setSeedbedAccessChecked(true))
  }, [isAuthenticated])

  const fetchCourses = useCallback(async () => {
    setLoading(true)
    setError(false)
    try {
      const params = new URLSearchParams()
      params.set("page", page.toString())
      params.set("limit", "12")
      if (search) params.set("search", search)
      if (filterCategory) params.set("category", filterCategory)
      if (filterDifficulty) params.set("difficulty", filterDifficulty)

      const { data } = await api.get(`/academy/courses?${params.toString()}`)
      setCourses(data.data.courses)
      setTotalPages(data.data.pagination.totalPages)
      setTotalResults(data.data.pagination.total)
    } catch (err) {
      console.error("Error loading courses:", err)
      setCourses([])
      setError(true)
    } finally {
      setLoading(false)
    }
  }, [page, search, filterCategory, filterDifficulty])

  useEffect(() => {
    if (!seedbedAccessChecked || !enrolledAccessChecked || isPersonalizedMode) {
      if (isPersonalizedMode) setLoading(false)
      return
    }
    fetchCourses()
  }, [fetchCourses, seedbedAccessChecked, enrolledAccessChecked, isPersonalizedMode])

  useEffect(() => {
    if (!isAuthenticated) {
      setEnrollmentByCourseId({})
      setEnrolledCourses([])
      setEnrolledAccessChecked(true)
      return
    }
    setEnrolledAccessChecked(false)
    api
      .get("/academy/courses/enrolled/me")
      .then(({ data }) => {
        const enrolled = data.data as EnrolledCourseSummary[]
        const map: Record<string, CourseCardEnrollment> = {}
        for (const c of enrolled) {
          map[c.id] = { completedLessons: c.completed_lessons, totalLessons: c.total_lessons }
        }
        setEnrollmentByCourseId(map)
        setEnrolledCourses(enrolled)
      })
      .catch((err) => {
        console.error("Error loading enrollment progress:", err)
        setEnrolledCourses([])
      })
      .finally(() => setEnrolledAccessChecked(true))
  }, [isAuthenticated])

  const handleReset = () => {
    setSearch("")
    setFilterCategory("")
    setFilterDifficulty("")
    setPage(1)
  }

  const hasActiveFilters = search || filterCategory || filterDifficulty

  const displayedCourses = isPersonalizedMode
    ? personalizedCourses.filter((c) => !search || c.title.toLowerCase().includes(search.toLowerCase()))
    : courses

  return (
    <div className="min-h-screen selection:bg-primary/30">
      <SEO
        config={siteConfig}
        title="Catálogo de Cursos | Aprende Cohetería y Aeroespacial"
        description="Explora todos los cursos STEM aeroespaciales. Cohetería, satélites, aviación, CanSat y más. Instructores certificados por ALAS Corporation."
        path="/courses"
        keywords={['catálogo cursos aeroespaciales', 'cursos cohetería', 'cursos satélites', 'STEM online Colombia']}
      />
      <div className="relative overflow-hidden border-b border-border py-8 sm:py-10">

        <div className="section-shell relative">
          <PageHeader
            title="Catálogo de especialización"
            description="Cursos de ingeniería aeroespacial con certificado institucional."
          />
        </div>
      </div>

      <div className="section-shell py-12">
        <div className="flex flex-col gap-10 lg:flex-row">
          {!isPersonalizedMode && filtersOpen && (
            <div
              className="fixed inset-0 z-40 bg-black/60 lg:hidden"
              onClick={() => setFiltersOpen(false)}
            />
          )}
          {isPersonalizedMode ? (
            <aside className="w-64 shrink-0 lg:block">
              <div className="sticky top-12 space-y-6">
                <div>
                  <h3 className="mb-5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {seedbedInfo.length > 0 ? "Tu semillero" : "Mis cursos"}
                  </h3>
                  {seedbedInfo.length > 0 ? (
                    <div className="space-y-4">
                      {seedbedInfo.map((s) => (
                        <div key={s.seedbedName}>
                          <p className="text-sm font-semibold text-foreground">{s.seedbedName}</p>
                          <p className="text-xs text-muted-foreground">{s.institutionName}</p>
                        </div>
                      ))}
                    </div>
                  ) : null}
                  <p className="mt-6 text-xs text-muted-foreground leading-relaxed">
                    {seedbedInfo.length > 0
                      ? "Estos son los cursos habilitados para ti por tu semillero."
                      : "Estos son los cursos en los que estás inscrito."}
                  </p>
                </div>

                <div className="border-t border-border pt-5">
                   <p className="text-xs font-semibold text-foreground mb-1">Soporte académico</p>
                   <p className="text-xs text-muted-foreground leading-relaxed">
                     {seedbedInfo.length > 0
                       ? "Si tienes problemas para acceder a un curso asignado, contacta a tu director de semillero."
                       : "Si tienes problemas para acceder a un curso, contáctanos desde el panel de soporte."}
                   </p>
                </div>

                <Link
                  to="/courses?view=all"
                  className="block text-center text-xs font-semibold text-muted-foreground hover:text-primary transition-colors"
                >
                  Ver catálogo completo →
                </Link>
              </div>
            </aside>
          ) : (
            <aside
              className={`w-64 shrink-0 lg:block ${
                filtersOpen
                  ? "fixed inset-y-0 left-0 z-50 w-72 overflow-y-auto bg-background p-6 lg:static lg:z-auto lg:w-64 lg:bg-transparent lg:p-0"
                  : "hidden"
              }`}
            >
              <div className="sticky top-12 space-y-6">
                <div className="flex items-center justify-between lg:hidden">
                  <span className="text-sm font-bold text-foreground">Filtros</span>
                  <button
                    onClick={() => setFiltersOpen(false)}
                    aria-label="Cerrar filtros"
                    className="rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-foreground"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <div>
                  <h3 className="mb-5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Programas
                  </h3>
                  <div className="space-y-1">
                    <button
                      onClick={() => { setFilterCategory(""); setPage(1) }}
                      className={`flex w-full items-center justify-between rounded-lg px-4 py-3 text-sm transition-colors ${
                        filterCategory === ""
                          ? "bg-foreground/10 font-semibold text-foreground"
                          : "font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
                      }`}
                    >
                      Todos
                    </button>
                    {programs.map((program) => (
                      <button
                        key={program.category}
                        onClick={() => { setFilterCategory(program.category); setPage(1) }}
                        className={`flex w-full items-center justify-between rounded-lg px-4 py-3 text-left text-sm transition-colors ${
                          filterCategory === program.category
                            ? "bg-foreground/10 font-semibold text-foreground"
                            : "font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
                        }`}
                      >
                        <span className="truncate">{program.label}</span>
                      </button>
                    ))}
                  </div>

                  <div className="my-6 h-px bg-border" />

                  <h3 className="mb-5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Dificultad
                  </h3>
                  <div className="space-y-1">
                    {[
                      { label: "Todos", value: "" },
                      { label: "Básico", value: "beginner" },
                      { label: "Intermedio", value: "intermediate" },
                      { label: "Avanzado", value: "advanced" },
                    ].map((opt) => (
                      <button
                        key={opt.label}
                        onClick={() => { setFilterDifficulty(opt.value); setPage(1) }}
                        className={`flex w-full items-center justify-between rounded-lg px-4 py-3 text-sm transition-colors ${
                          filterDifficulty === opt.value
                            ? "border border-border bg-muted font-semibold text-foreground"
                            : "font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>

                  {hasActiveFilters && (
                    <button
                      onClick={handleReset}
                      className="mt-8 flex w-full items-center justify-center gap-2 rounded-lg border border-border px-4 py-3 text-xs font-semibold text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                    >
                      <X className="h-4 w-4" />
                      Limpiar filtros
                    </button>
                  )}
                </div>

                <div className="border-t border-border pt-5">
                   <p className="text-xs font-semibold text-foreground mb-1">Soporte académico</p>
                   <p className="text-xs text-muted-foreground leading-relaxed">Si tienes problemas para acceder a un curso asignado, contacta a tu director de semillero.</p>
                </div>
              </div>
            </aside>
          )}

          <div className="min-w-0 flex-1">
            <div className="mb-10 flex gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="¿Qué quieres aprender hoy? Busca tecnología o nombre del curso..."
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1) }}
                  className="h-14 w-full rounded-lg border border-input bg-transparent pl-12 pr-6 text-base text-foreground placeholder:text-muted-foreground transition-colors focus-visible:border-ring focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
                />
              </div>
              {!isPersonalizedMode && (
                <button
                  onClick={() => setFiltersOpen(!filtersOpen)}
                  className="flex h-14 items-center gap-2 rounded-lg border border-border bg-background px-6 text-sm font-bold text-muted-foreground transition-colors hover:bg-muted hover:text-foreground lg:hidden"
                >
                  <SlidersHorizontal className="h-5 w-5" />
                  {hasActiveFilters && (
                    <span className="h-2 w-2 rounded-full bg-primary animate-pulse motion-reduce:animate-none" aria-hidden="true" />
                  )}
                </button>
              )}
            </div>

            {!isPersonalizedMode && (
              <ActiveFiltersBar
                search={search}
                category={filterCategory}
                difficulty={filterDifficulty}
                totalResults={totalResults}
                onRemoveSearch={() => setSearch("")}
                onRemoveCategory={() => setFilterCategory("")}
                onRemoveDifficulty={() => setFilterDifficulty("")}
                onClearAll={handleReset}
              />
            )}

            {loading ? (
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="h-80 animate-pulse rounded-xl bg-muted" />
                ))}
              </div>
            ) : !isPersonalizedMode && error ? (
              <div className="flex flex-col items-center justify-center rounded-xl border border-border bg-card py-32 text-center">
                <div className="h-20 w-20 rounded-xl bg-muted flex items-center justify-center mb-6">
                   <X className="h-10 w-10 text-muted-foreground" />
                </div>
                <p className="text-2xl font-semibold tracking-tight text-foreground">No pudimos cargar los cursos</p>
                <p className="mt-2 text-muted-foreground max-w-xs">
                  Hubo un problema de conexión. Intenta de nuevo en unos segundos.
                </p>
                <button
                  onClick={fetchCourses}
                  className="mt-8 rounded-lg bg-primary px-8 py-3 text-sm font-semibold text-primary-foreground motion-press transition-[filter,transform] hover:brightness-95"
                >
                  Reintentar
                </button>
              </div>
            ) : displayedCourses.length === 0 ? (
              <PageEmpty
                icon={<Users className="h-8 w-8" />}
                title={
                  isPersonalizedMode
                    ? seedbedInfo.length > 0
                      ? "Este semillero aún no tiene cursos asignados"
                      : "Aún no estás inscrito en ningún curso"
                    : "Aún no hay cursos que coincidan con tus filtros"
                }
                description={
                  isPersonalizedMode
                    ? seedbedInfo.length > 0
                      ? "Contacta a tu director de semillero para más información."
                      : "Explora el catálogo público para inscribirte en tu primer curso."
                    : "Prueba con otros filtros o reinicia la búsqueda."
                }
                action={
                  !isPersonalizedMode ? (
                    <button
                      onClick={handleReset}
                      className="inline-flex min-h-11 items-center rounded-lg bg-primary px-6 text-sm font-semibold text-primary-foreground motion-press transition-[filter,transform] hover:brightness-95"
                    >
                      Reiniciar búsqueda
                    </button>
                  ) : seedbedInfo.length === 0 ? (
                    <Link
                      to="/courses?view=all"
                      className="inline-flex min-h-11 items-center rounded-lg bg-primary px-6 text-sm font-semibold text-primary-foreground motion-press transition-[filter,transform] hover:brightness-95"
                    >
                      Ver catálogo completo
                    </Link>
                  ) : undefined
                }
              />
            ) : (
              <div className="grid gap-x-6 gap-y-12 lg:grid-cols-3">
                {displayedCourses.map((course, i) => {
                  if (!introClass.current.has(course.id)) {
                    introClass.current.set(course.id, staggerUsed.current ? "motion-fade" : "motion-stagger-item")
                  }
                  if (i === displayedCourses.length - 1) staggerUsed.current = true
                  return (
                    <div key={course.id} className={introClass.current.get(course.id)} style={{ "--i": Math.min(i, 7) } as CSSProperties}>
                      <CourseCard course={course} enrollment={enrollmentByCourseId[course.id]} />
                    </div>
                  )
                })}
              </div>
            )}

            {!isPersonalizedMode && totalPages > 1 && (
              <div className="mt-16 flex items-center justify-center gap-4">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="min-h-11 rounded-lg border border-border bg-background px-6 py-3 text-sm font-semibold text-foreground transition-colors hover:bg-muted disabled:pointer-events-none disabled:opacity-50"
                >
                  Anterior
                </button>
                <div className="flex gap-2">
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                    const p = i + 1
                    return (
                      <button
                        key={p}
                        onClick={() => setPage(p)}
                        className={`h-11 w-11 rounded-lg text-sm font-semibold transition-colors ${
                          p === page
                            ? "bg-primary text-primary-foreground"
                            : "border border-border text-muted-foreground hover:bg-muted"
                        }`}
                      >
                        {p}
                      </button>
                    )
                  })}
                </div>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="min-h-11 rounded-lg border border-border bg-background px-6 py-3 text-sm font-semibold text-foreground transition-colors hover:bg-muted disabled:pointer-events-none disabled:opacity-50"
                >
                  Siguiente
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

